import React from 'react';
import { motion } from 'framer-motion';
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown, 
  BarChart3, 
  PieChart, 
  Calendar,
  MapPin,
  Users,
  Building2,
  Target,
  Info,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';

interface RevenueModalProps {
  data: {
    total_revenue: number;
    monthly_growth: number;
    revenue_analytics: {
      monthly_revenue: Array<{
        month: string;
        revenue: number;
        growth: number;
      }>;
      revenue_by_segment: Array<{
        segment: string;
        revenue: number;
        percentage: number;
      }>;
      top_clients: Array<{
        client: string;
        revenue: number;
        percentage: number;
      }>;
    };
  };
}

export const RevenueModal: React.FC<RevenueModalProps> = ({ data }) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  const COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444'];

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="p-6 rounded-xl shadow-lg border relative overflow-hidden"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-primary)',
            boxShadow: 'var(--shadow-md)'
          }}
        >
          <div className="relative flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg">
              <DollarSign className="h-6 w-6 text-white" />
            </div>
            <div className="text-right">
              <p 
                className="text-sm font-medium"
                style={{ color: 'var(--text-primary)' }}
              >
                Receita Total
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <p 
              className="text-2xl font-bold break-words"
              style={{ color: 'var(--text-primary)' }}
            >
              {formatCurrency(data.total_revenue)}
            </p>
            <p 
              className="text-xs"
              style={{ color: 'var(--text-tertiary)' }}
            >
              Acumulado do período
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="p-6 rounded-xl shadow-lg border relative overflow-hidden"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-primary)',
            boxShadow: 'var(--shadow-md)'
          }}
        >
          <div className="relative flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg">
              <TrendingUp className="h-6 w-6 text-white" />
            </div>
            <div className="text-right">
              <p 
                className="text-sm font-medium"
                style={{ color: 'var(--text-primary)' }}
              >
                Crescimento Mensal
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <p 
              className="text-2xl font-bold"
              style={{ color: 'var(--text-primary)' }}
            >
              +{data.monthly_growth.toFixed(1)}%
            </p>
            <p 
              className="text-xs"
              style={{ color: 'var(--text-tertiary)' }}
            >
              vs mês anterior
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="p-6 rounded-xl shadow-lg border relative overflow-hidden"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-primary)',
            boxShadow: 'var(--shadow-md)'
          }}
        >
          <div className="relative flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 shadow-lg">
              <BarChart3 className="h-6 w-6 text-white" />
            </div>
            <div className="text-right">
              <p 
                className="text-sm font-medium"
                style={{ color: 'var(--text-primary)' }}
              >
                Média Mensal
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <p 
              className="text-2xl font-bold break-words"
              style={{ color: 'var(--text-primary)' }}
            >
              {formatCurrency(data.total_revenue / 6)}
            </p>
            <p 
              className="text-xs"
              style={{ color: 'var(--text-tertiary)' }}
            >
              Últimos 6 meses
            </p>
          </div>
        </motion.div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Revenue Trend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="rounded-xl p-6 shadow border"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-primary)'
          }}
        >
          <h3 
            className="text-xl font-semibold mb-6 flex items-center"
            style={{ color: 'var(--text-primary)' }}
          >
            <LineChart 
              className="h-5 w-5 mr-2" 
              style={{ color: 'var(--primary)' }}
            />
            Tendência Mensal de Receita
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data.revenue_analytics.monthly_revenue}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'var(--bg-card)',
                  border: '1px solid var(--border-primary)',
                  borderRadius: '8px'
                }}
                formatter={(value: number) => [formatCurrency(value), 'Receita']}
              />
              <Area 
                type="monotone" 
                dataKey="revenue" 
                stroke="#10b981" 
                fill="#10b981" 
                fillOpacity={0.3}
              />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Revenue by Segment */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="rounded-xl p-6 shadow border"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-primary)'
          }}
        >
          <h3 
            className="text-xl font-semibold mb-6 flex items-center"
            style={{ color: 'var(--text-primary)' }}
          >
            <PieChart 
              className="h-5 w-5 mr-2" 
              style={{ color: 'var(--primary)' }}
            />
            Receita por Segmento
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <RechartsPieChart>
              <Pie
                data={data.revenue_analytics.revenue_by_segment}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ segment, percentage }) => `${segment} (${percentage.toFixed(1)}%)`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="revenue"
              >
                {data.revenue_analytics.revenue_by_segment.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'var(--bg-card)',
                  border: '1px solid var(--border-primary)',
                  borderRadius: '8px'
                }}
                formatter={(value: number) => [formatCurrency(value), 'Receita']}
              />
            </RechartsPieChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Top Clients */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="rounded-xl p-6 shadow border"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-primary)'
          }}
      >
        <h3 
          className="text-xl font-semibold mb-6 flex items-center"
          style={{ color: 'var(--text-primary)' }}
        >
          <Users 
            className="h-5 w-5 mr-2" 
            style={{ color: 'var(--primary)' }}
          />
          Top Clientes por Receita
        </h3>
        <div className="space-y-4">
          {data.revenue_analytics.top_clients.map((client, index) => (
            <div 
              key={index} 
              className="flex items-center justify-between p-4 rounded-lg"
              style={{
                backgroundColor: 'var(--bg-tertiary)',
                opacity: 0.6
              }}
            >
              <div className="flex items-center space-x-3">
                <div 
                  className="h-8 w-8 rounded-full flex items-center justify-center"
                  style={{
                    backgroundColor: 'var(--bg-tertiary)',
                    opacity: 0.8
                  }}
                >
                  <span 
                    className="text-sm font-bold"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {index + 1}
                  </span>
                </div>
                <div>
                  <h4 
                    className="font-semibold"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {client.client}
                  </h4>
                  <p 
                    className="text-sm"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {client.percentage.toFixed(1)}% do total
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div 
                  className="text-lg font-bold"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {formatCurrency(client.revenue)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Data Source Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="p-4 rounded-lg border relative overflow-hidden"
        style={{
          backgroundColor: 'var(--bg-card)',
          borderColor: 'var(--border-primary)',
          boxShadow: 'var(--shadow-md)'
        }}
      >
        <div className="relative">
          <div className="flex items-start space-x-3">
            <Info 
              className="h-5 w-5 mt-0.5 flex-shrink-0" 
              style={{ color: 'var(--text-tertiary)' }}
            />
            <div>
              <h4 
                className="font-semibold mb-1"
                style={{ color: 'var(--text-primary)' }}
              >
                Origem dos Dados
              </h4>
              <p 
                className="text-sm"
                style={{ color: 'var(--text-secondary)' }}
              >
                Os dados de receita são coletados automaticamente dos sistemas de faturamento e CRM, 
                atualizados em tempo real. Incluem receitas de todos os segmentos de clientes e 
                são consolidados mensalmente para análise de tendências.
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
